import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BASE_URL, POST_USER, UPLOAD_IMG, PUT_USER } from '../utlilities/config';
import { FiUser, FiCode, FiBriefcase, FiCalendar, FiLock, FiImage, FiCheck, FiX } from 'react-icons/fi';
import { format } from 'date-fns';

// Animated loader component
const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Processing...</p>
    </div>
  </div>
);

const AddUserModal = ({ setUserModal, getData, iD, setSelectedUserId, userDetails, setSelectedUserDetails }) => {
  const [loading, setLoading] = useState(false);
  const [signImage, setSignImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(userDetails?.SinatureUrl || null);
  const token = localStorage.getItem('accessToken');
  
  const isEditing = Boolean(iD);

  // Create refs for form fields to enable tab navigation
  const nameRef = useRef(null);
  const codeRef = useRef(null);
  const designationRef = useRef(null);
  const joiningDateRef = useRef(null);
  const passwordRef = useRef(null);
  const signatureRef = useRef(null);
  const saveButtonRef = useRef(null);
  const maxDiscountRef = useRef(null);
  useEffect(() => {
    // Focus the first input field when modal opens
    nameRef.current?.focus();
  }, []);

  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      UserCode: userDetails?.UserCode || '',
      UserName: userDetails?.UserName || '',
      PasswordHash: isEditing ? '' : (userDetails?.UserCode || ''), // Don't show existing password
      Designation: userDetails?.Designation || '',
      JoiningDate: userDetails?.JoiningDate ? 
        format(new Date(userDetails.JoiningDate), 'yyyy-MM-dd') : '',
      SinatureUrl: userDetails?.SinatureUrl || '',
      MaxDiscount: userDetails?.Max_Discount_Allowed || 0,
    },
    validationSchema: Yup.object({
      UserCode: Yup.string().required('User Code is required'),
      UserName: Yup.string().required('User Name is required'),
      PasswordHash: isEditing ? Yup.string() : Yup.string().required('Password is required'),
      Designation: Yup.string().required('Designation is required'),
      JoiningDate: Yup.date().required('Joining Date is required'),
      MaxDiscount: Yup.number().required('Max Discount is required'),
    }),
    
    onSubmit: async (values) => {
      setLoading(true);
      
      try {
        // Handle image upload if there's a new image
        if (signImage) {
          let headerImageUrlLocal = '';
          const headerFormData = new FormData();
          headerFormData.append('file', signImage);
  
          const headerResponse = await axios.post(`${BASE_URL}${UPLOAD_IMG}`, headerFormData, {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`,
            },
          });
  
          if (headerResponse.status === 200) {
            headerImageUrlLocal = headerResponse.data.url;
            values.SinatureUrl = headerImageUrlLocal;
          } else {
            throw new Error('Failed to upload signature image');
          }
        }

        // Create data object based on whether we're updating or creating
        const data = {
          UserCode: values.UserCode,
          UserName: values.UserName,
          Designation: values.Designation,
          JoiningDate: values.JoiningDate,
          SinatureUrl: values.SinatureUrl,
          MaxDiscount: values.MaxDiscount,
          ...(isEditing ? { IsActive: true } : { PasswordHash: values.PasswordHash }),
        };

        // Make appropriate request based on editing or creating
        const response = await axios({
          method: isEditing ? 'post' : 'post',
          url: `${BASE_URL}${isEditing ? PUT_USER : POST_USER}`,
          data: data,
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            ...(isEditing && { 'UserID': iD }),
          },
        });

        if (response.status === 200) {
          // Success! Close modal and refresh data
          setUserModal(false);
          getData();
          if (setSelectedUserId) setSelectedUserId(null);
          if (setSelectedUserDetails) setSelectedUserDetails(null);
        }
      } catch (error) {
        console.error('Error saving user:', error);
        // Could implement error toast/notification here
      } finally {
        setLoading(false);
      }
    },
  });

  // Handle image input and preview
  const handleInputImage = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSignImage(file);
      setImagePreview(URL.createObjectURL(file));
      formik.setFieldValue('SinatureUrl', file.name);
    }
  };

  const handleCancel = () => {
    setUserModal(false);
    if (setSelectedUserId) setSelectedUserId(null);
    if (setSelectedUserDetails) setSelectedUserDetails(null);
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && <Spinner />}
      
      <div className="bg-white w-3/5 rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <FiUser className="mr-2" /> {isEditing ? 'Edit User' : 'Add New User'}
          </h2>
          <button 
            onClick={handleCancel}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* User Name Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  User Name <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiUser />
                </div>
                <input
                  type="text"
                  name="UserName"
                  placeholder="Enter User Name"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.UserName && formik.errors.UserName 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.UserName}
                  onChange={(e) => {
                    formik.setFieldValue('UserName', e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  ref={nameRef}
                  onKeyDown={(e) => handleKeyDown(e, codeRef)}
                />
              </div>
              {formik.touched.UserName && formik.errors.UserName && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.UserName}</p>
              )}
            </div>

            {/* User Code Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  User Code <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiCode />
                </div>
                <input
                  type="text"
                  name="UserCode"
                  placeholder="Enter User Code"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.UserCode && formik.errors.UserCode 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.UserCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={codeRef}
                  onKeyDown={(e) => handleKeyDown(e, designationRef)}
                />
              </div>
              {formik.touched.UserCode && formik.errors.UserCode && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.UserCode}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Designation Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Designation <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiBriefcase />
                </div>
                <select
                  name="Designation"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.Designation && formik.errors.Designation 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 appearance-none`}
                  value={formik.values.Designation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={designationRef}
                  onKeyDown={(e) => handleKeyDown(e, joiningDateRef)}
                >
                  <option value="" disabled>Select Designation</option>
                  <option value="Staff">Staff</option>
                  <option value="Admin">Admin</option>
                  <option value="Trainee">Trainee</option>
                  <option value="Intern">Intern</option>
                  <option value="Incharge">Incharge</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
              {formik.touched.Designation && formik.errors.Designation && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.Designation}</p>
              )}
            </div>

            {/* Joining Date Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Joining Date <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiCalendar />
                </div>
                <input
                  type="date"
                  name="JoiningDate"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.JoiningDate && formik.errors.JoiningDate 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.JoiningDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={joiningDateRef}
                  onKeyDown={(e) => handleKeyDown(e, passwordRef)}
                />
              </div>
              {formik.touched.JoiningDate && formik.errors.JoiningDate && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.JoiningDate}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Password Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Password {!isEditing && <span className="text-red-500 ml-1">*</span>}
                  {isEditing && <span className="text-gray-500 ml-1 text-xs">(Leave blank to keep current)</span>}
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiLock />
                </div>
                <input
                  type="password"
                  name="PasswordHash"
                  placeholder={isEditing ? "Leave blank to keep current password" : "Enter Password"}
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.PasswordHash && formik.errors.PasswordHash 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.PasswordHash}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={passwordRef}
                  onKeyDown={(e) => handleKeyDown(e, signatureRef)}
                />
              </div>
              {formik.touched.PasswordHash && formik.errors.PasswordHash && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.PasswordHash}</p>
              )}
            </div>
            
            {/* Signature Field */}
            <div className="w-full">
              <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Signature {!isEditing && <span className="text-red-500 ml-1">*</span>}
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiImage />
                </div>
                <input
                  type="file"
                  id="SinatureUrl"
                  accept="image/*"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.SinatureUrl && formik.errors.SinatureUrl 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100`}
                  onChange={handleInputImage}
                  ref={signatureRef}
                  onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                />
              </div>
              
              {/* Signature Preview */}
              {(imagePreview || formik.values.SinatureUrl) && (
                <div className="mt-3 flex items-center">
                  <div className="p-2 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-center h-24 w-40">
                    <img
                      src={imagePreview || formik.values.SinatureUrl}
                      alt="Signature Preview"
                      className="max-h-20 max-w-32 object-contain"
                    />
                  </div>
                  <p className="ml-3 text-sm text-gray-600">Signature Preview</p>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
          <div className="w-full">
            <div className="flex justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 flex items-center">
                  Max Discount Percentage <span className="text-red-500 ml-1">*</span>
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                  <FiCode />
                </div>
                <input
                  type="number"
                  name="MaxDiscount"
                  placeholder="Enter User Code"
                  className={`w-full pl-10 pr-3 py-3 border ${
                    formik.touched.MaxDiscount && formik.errors.MaxDiscount 
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                      : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                  } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                  value={formik.values.MaxDiscount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  ref={maxDiscountRef}
                />
              </div>
              {formik.touched.MaxDiscount && formik.errors.MaxDiscount && (
                <p className="mt-1 text-sm text-red-500">{formik.errors.MaxDiscount}</p>
              )}
            </div>

          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
              onClick={handleCancel}
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              ref={saveButtonRef}
            >
              <FiCheck className="mr-2" /> {isEditing ? 'Update User' : 'Save User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;